import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Star, Quote, BookOpen, TrendingUp, Globe } from "lucide-react";
import { Reveal, MaskLine } from "../components/Reveal";
import { TRUST, PROGRAMS, WHY_US, TESTIMONIALS, HERO_IMAGE } from "../data/site";

const ICONS = { BookOpen, TrendingUp, Globe };

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative flex items-center overflow-hidden min-h-[92svh] pt-32 pb-16"
      >
        {/* Mural background */}
        <motion.img
          src={HERO_IMAGE}
          alt="The Thinkstitute mural — Education Beyond Textbooks"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 w-full h-full object-cover object-[68%_center]"
        />
        {/* Legibility scrims */}
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/90 to-cream/10 md:via-cream/70 md:to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-cream to-transparent" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10 w-full">
          <motion.div style={{ y: textY }} className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-brand text-cream px-4 py-2 text-sm font-bold mb-6"
            >
              <Sparkles size={16} /> Vesu, Surat · Grades 7–12
            </motion.span>

            <h1 className="font-display font-extrabold text-navy leading-[0.9] text-5xl sm:text-7xl lg:text-[6.5rem]">
              <MaskLine delay={0.25}>EDUCATION</MaskLine>
              <MaskLine delay={0.4} className="text-marigold">BEYOND</MaskLine>
              <MaskLine delay={0.55}>TEXTBOOKS</MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="mt-7 max-w-xl text-lg sm:text-xl text-navy/80 font-medium"
            >
              An after-school coaching institute for Grades 7–12 in Vesu, Surat —
              CBSE, Commerce &amp; IB MYP, taught by a team that goes deeper.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/admissions"
                data-testid="hero-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-marigold text-white font-bold text-lg px-8 py-4 shadow-[0_7px_0_0_#c85c14] hover:translate-y-0.5 hover:shadow-[0_4px_0_0_#c85c14] transition-all"
              >
                Book a Free Consultation
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link
                to="/programs"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream font-bold text-lg px-8 py-4 hover:bg-indigo-brand transition-colors"
              >
                Explore Programs
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-navy text-cream py-14" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-8">
          {TRUST.map((s, i) => (
            <Reveal key={s.big} delay={i * 0.1} className="text-center sm:border-l sm:first:border-l-0 border-cream/15">
              <p className="font-display font-extrabold text-3xl sm:text-4xl text-marigold">{s.big}</p>
              <p className="mt-1 text-cream/70 text-sm sm:text-base px-4">{s.small}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROGRAMS PREVIEW */}
      <section className="py-24" data-testid="programs-preview">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <span className="font-bold text-teal-brand text-lg">OUR PROGRAMS</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy mt-2">
              Built for every stage of the journey.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {PROGRAMS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
              <Reveal key={p.id} delay={i * 0.12}>
                <motion.div
                  whileHover={{ y: -8 }}
                  className="h-full rounded-3xl bg-white border border-navy/10 p-8 flex flex-col shadow-sm hover:shadow-xl transition-shadow"
                >
                  <span
                    className="grid place-items-center w-12 h-12 rounded-2xl text-white mb-5 -rotate-6"
                    style={{ backgroundColor: p.color }}
                  >
                    {Icon && <Icon size={24} />}
                  </span>
                  <span
                    className="inline-block w-fit rounded-full px-4 py-1.5 text-base font-bold text-white"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.tag}
                  </span>
                  <h3 className="font-display font-extrabold text-2xl text-navy mt-4">{p.title}</h3>
                  <p className="mt-2 text-navy/70 flex-1">{p.hook}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.subjects.map((s) => (
                      <span key={s} className="text-xs font-semibold rounded-full bg-cream px-3 py-1 text-navy/70">{s}</span>
                    ))}
                  </div>
                  <Link
                    to="/programs"
                    className="mt-6 inline-flex items-center gap-1 font-bold text-marigold hover:gap-2 transition-all"
                    data-testid={`program-link-${p.id}`}
                  >
                    Learn more <ArrowRight size={16} />
                  </Link>
                </motion.div>
              </Reveal>
            );})}
          </div>
        </div>
      </section>

      {/* WHY US - numbered manifesto */}
      <section className="py-24 bg-indigo-brand text-cream relative overflow-hidden" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-16">
            <span className="font-bold text-marigold">WHY THE THINKSTITUTE</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-2">
              A different kind of coaching.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
            {WHY_US.map((w, i) => (
              <Reveal key={w.n} delay={i * 0.08}>
                <div className="flex gap-6 border-t border-cream/20 pt-6">
                  <span className="font-display font-extrabold text-5xl text-teal-brand shrink-0">{w.n}</span>
                  <div>
                    <h3 className="font-display font-bold text-2xl">{w.title}</h3>
                    <p className="mt-2 text-cream/75">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 bg-marigold overflow-hidden" data-testid="marquee">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center">
              {["Think Deeper", "Question More", "Learn Joyfully", "Grow Boldly", "Education Beyond Textbooks"].map((t) => (
                <span key={t} className="font-display font-extrabold text-3xl sm:text-4xl text-navy px-8 flex items-center gap-8">
                  {t} <Star fill="#14213D" size={22} />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <span className="font-bold text-teal-brand">LOVED BY FAMILIES</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy mt-2">
              Real stories, real growth.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <div className="h-full rounded-3xl bg-white border border-navy/10 p-8 flex flex-col">
                  <Quote className="text-marigold" size={34} />
                  <p className="mt-4 text-navy/80 text-lg flex-1 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                  <div className="mt-6">
                    <p className="font-display font-bold text-navy">{t.name}</p>
                    <p className="text-sm text-navy/60">{t.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA BANNER */}
      <section className="pb-24" data-testid="final-cta">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="relative rounded-[2.5rem] bg-teal-brand text-white p-10 sm:p-16 overflow-hidden">
              <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10" />
              <div className="absolute -bottom-20 left-10 w-72 h-72 rounded-full bg-navy/10" />
              <div className="relative z-10 max-w-2xl">
                <h2 className="font-display font-extrabold text-4xl sm:text-6xl leading-tight">
                  Ready to see the difference?
                </h2>
                <p className="mt-4 text-white/85 text-lg">
                  Book a free, no-pressure consultation and our team will get
                  back to you within 48 hours.
                </p>
                <Link
                  to="/admissions"
                  data-testid="final-cta-btn"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy text-cream font-bold text-lg px-8 py-4 hover:bg-marigold transition-colors"
                >
                  Book a Free Consultation <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
